import React, { useEffect } from 'react';
import '../styles/FeeStructure.scss';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  DollarSign, Calendar, ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import StructuredData from '../components/StructuredData';

export default function FeeStructure() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const navItems = [
    { id: 'class-wise-fees', label: t('nav_class_fees') },
    { id: 'admission-fee', label: t('nav_admission_fee') },
    { id: 'monthly-tuition', label: t('nav_monthly_tuition') },
    { id: 'other-charges', label: t('nav_other_charges') },
    { id: 'payment-guidelines', label: t('nav_payment_guidelines') },
  ];

  const guidelines = [
    'Tuition fee must be deposited by the 10th of every calendar month at the school fee collection counter.',
    'A nominal late fee fine of ₹5 per day is applicable for fees received after the 10th of the month.',
    'Parents paying the annual tuition fee in a single lump sum advance at session commencement receive a 5% discount.',
    'Sibling concessions (15% discount on monthly tuition) are provided for the second child enrolled concurrently.',
    'All fee payments must be made in cash directly at the school office counter; no online transfer or UPI payment is accepted.',
    'Please collect and retain the official printed fee receipt issued by the school counter for verification.',
    'Defaulters with consecutive three-month unpaid dues will be flagged for review by the administrative office.'
  ];

  return (
    <div className="fee-structure-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>Fee Structure - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Comprehensive fee structure for Prabhat Tara Vidya Niketan Goraul: Class-wise fees, Admission fee, Monthly tuition, Transportation charges, and Payment guidelines."
        />
        <meta
          name="keywords"
          content="Prabhat Tara Vidya Niketan fees, school fee structure, admission fees, tuition fees, examination fees, transportation charges, uniform costs, payment guidelines"
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/fee-structure" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <div className="hero-inner">
          <span className="badge">
            <DollarSign size={16} /> Transparent & Affordable Quality Education
          </span>
          <h1>School Fee Structure</h1>
          <p>
            Academic Session 2026-2027 | Playgroup to Class 10 | Detailed breakdown of admission, tuition, transportation, and counter payment guidelines.
          </p>
        </div>
      </div>

      {/* Sticky Nav Pills Bar */}
      <div className="nav-pills-bar">
        <div className="nav-container">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div className="fee-content-wrapper">

        {/* 1. CLASS-WISE FEE STRUCTURE */}
        <section id="class-wise-fees" className="fee-card">
          <span className="card-tag">1. Master Schedule</span>
          <h2>Class-wise Fee Structure (Overview)</h2>
          <p className="card-desc">
            A unified view of admission registration, monthly tuition, and terminal examination fees across all class brackets.
          </p>

          <div className="table-responsive">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Fee Component</th>
                  <th>Play, LKG & UKG</th>
                  <th>Class 1 to 5</th>
                  <th>Class 6 to 8</th>
                  <th>Class 9 & 10</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Fee Component">Admission & Registration (One-time)</td>
                  <td data-label="Play, LKG & UKG" className="price-highlight">₹1,000</td>
                  <td data-label="Class 1-5" className="price-highlight">₹1,000</td>
                  <td data-label="Class 6-8" className="price-highlight">₹1,500</td>
                  <td data-label="Class 9 & 10" className="price-highlight">₹8,000</td>
                </tr>
                <tr>
                  <td data-label="Fee Component">Monthly Tuition Fee</td>
                  <td data-label="Play, LKG & UKG">₹500 / mo</td>
                  <td data-label="Class 1-5">₹500 / mo</td>
                  <td data-label="Class 6-8">₹600 / mo</td>
                  <td data-label="Class 9 & 10">₹1,200 / mo</td>
                </tr>
                <tr>
                  <td data-label="Fee Component">Half-yearly Examination Fee</td>
                  <td data-label="Play, LKG & UKG">₹200</td>
                  <td data-label="Class 1-5">₹200</td>
                  <td data-label="Class 6-8">₹250</td>
                  <td data-label="Class 9 & 10">₹300</td>
                </tr>
                <tr>
                  <td data-label="Fee Component">Annual Examination Fee</td>
                  <td data-label="Play, LKG & UKG">₹200</td>
                  <td data-label="Class 1-5">₹200</td>
                  <td data-label="Class 6-8">₹250</td>
                  <td data-label="Class 9 & 10">₹300</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 2. ADMISSION FEE */}
        <section id="admission-fee" className="fee-card">
          <span className="card-tag">2. One-Time Charges</span>
          <h2>Admission Fee Details</h2>
          <p className="card-desc">
            The admission fee is payable only once at the time of entry to Prabhat Tara Vidya Niketan and covers registration, student file creation, and administrative processing.
          </p>

          <div className="table-responsive">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Class Bracket</th>
                  <th>Admission & Registration Fee</th>
                  <th>Prospectus & ID Card</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Class Bracket">Playgroup, LKG & UKG</td>
                  <td data-label="Admission Fee" className="price-highlight">₹1,000</td>
                  <td data-label="Prospectus">Included</td>
                  <td data-label="Remarks">One-time enrollment</td>
                </tr>
                <tr>
                  <td data-label="Class Bracket">Primary Wing (Class 1 to 5)</td>
                  <td data-label="Admission Fee" className="price-highlight">₹1,000</td>
                  <td data-label="Prospectus">Included</td>
                  <td data-label="Remarks">Includes student badge</td>
                </tr>
                <tr>
                  <td data-label="Class Bracket">Middle Wing (Class 6 to 8)</td>
                  <td data-label="Admission Fee" className="price-highlight">₹1,500</td>
                  <td data-label="Prospectus">Included</td>
                  <td data-label="Remarks">Includes library card</td>
                </tr>
                <tr>
                  <td data-label="Class Bracket">Secondary Wing (Class 9 & 10)</td>
                  <td data-label="Admission Fee" className="price-highlight">₹8,000</td>
                  <td data-label="Prospectus">Included</td>
                  <td data-label="Remarks">Includes board registration & lab orientation</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 3. MONTHLY TUITION FEE */}
        <section id="monthly-tuition" className="fee-card">
          <span className="card-tag">3. Recurring Schedule</span>
          <h2>Monthly Tuition Fee Breakdown</h2>
          <p className="card-desc">
            Tuition fees cover continuous classroom teaching, smart digital modules, computer lab sessions, sports coaching, and library access.
          </p>

          <div className="table-responsive">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Class / Standard</th>
                  <th>Monthly Tuition</th>
                  <th>Quarterly Equivalent (3 Mo)</th>
                  <th>Annual Total (12 Mo)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Class">Playgroup, LKG & UKG</td>
                  <td data-label="Monthly" className="price-highlight">₹500</td>
                  <td data-label="Quarterly">₹1,500</td>
                  <td data-label="Annual">₹6,000</td>
                </tr>
                <tr>
                  <td data-label="Class">Classes 1 to 5</td>
                  <td data-label="Monthly" className="price-highlight">₹500</td>
                  <td data-label="Quarterly">₹1,500</td>
                  <td data-label="Annual">₹6,000</td>
                </tr>
                <tr>
                  <td data-label="Class">Classes 6 to 8</td>
                  <td data-label="Monthly" className="price-highlight">₹600</td>
                  <td data-label="Quarterly">₹1,800</td>
                  <td data-label="Annual">₹7,200</td>
                </tr>
                <tr>
                  <td data-label="Class">Classes 9 & 10</td>
                  <td data-label="Monthly" className="price-highlight">₹1,200</td>
                  <td data-label="Quarterly">₹3,600</td>
                  <td data-label="Annual">₹14,400</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. OTHER CHARGES */}
        <section id="other-charges" className="fee-card">
          <span className="card-tag">4. Transportation, Uniform & Accessories</span>
          <h2>Other Applicable Charges</h2>

          <h3 className="sub-heading">A. Monthly School Bus / Van Transportation</h3>
          <p className="card-desc">
            Calculated on a distance radius slab covering Rajkhand, Goraul, and surrounding Vaishali localities.
          </p>
          <div className="table-responsive">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Distance Radius</th>
                  <th>Monthly Bus Charge</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Distance Radius">01 km to 03 km</td>
                  <td data-label="Charge" className="price-highlight">₹500 / month</td>
                </tr>
                <tr>
                  <td data-label="Distance Radius">04 km to 06 km</td>
                  <td data-label="Charge" className="price-highlight">₹600 / month</td>
                </tr>
                <tr>
                  <td data-label="Distance Radius">06 km to 08 km</td>
                  <td data-label="Charge" className="price-highlight">₹700 / month</td>
                </tr>
                <tr>
                  <td data-label="Distance Radius">08 km to 10 km</td>
                  <td data-label="Charge" className="price-highlight">₹900 / month</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="sub-heading">B. School Uniform & Accessories Price List</h3>
          <div className="uniform-schedule-callout">
            <div className="callout-text">
              <Calendar className="callout-icon" size={24} />
              <div>
                <strong>School Dress Code Schedule</strong>
                <p>Check the daily uniform timetable (Regular uniform vs House dress) and guidelines.</p>
              </div>
            </div>
            <Link to="/uniform-schedule" className="btn-callout-link">
              <span>View Uniform Schedule</span>
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="table-responsive">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Uniform Item / Size</th>
                  <th>Price / Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Item">Uniform Set (Size 24-28) with Tie & Belt</td>
                  <td data-label="Cost" className="price-highlight">₹650</td>
                </tr>
                <tr>
                  <td data-label="Item">Uniform Set (Size 36-40)</td>
                  <td data-label="Cost" className="price-highlight">₹1,000</td>
                </tr>
                <tr>
                  <td data-label="Item">Short Belt</td>
                  <td data-label="Cost">₹50</td>
                </tr>
                <tr>
                  <td data-label="Item">Long Belt</td>
                  <td data-label="Cost">₹80</td>
                </tr>
                <tr>
                  <td data-label="Item">Short Tie</td>
                  <td data-label="Cost">₹50</td>
                </tr>
                <tr>
                  <td data-label="Item">Long Tie</td>
                  <td data-label="Cost">₹100</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 5. PAYMENT GUIDELINES */}
        <section id="payment-guidelines" className="fee-card">
          <span className="card-tag">5. Fee Rules & Counter Collection</span>
          <h2>Fee Payment Guidelines & Counter Notice</h2>
          <p className="card-desc">
            To maintain administrative efficiency and provide uninterrupted academic services, parents are requested to observe the following norms.
          </p>

          <div className="guidelines-grid">
            {guidelines.map((g, idx) => (
              <div key={idx} className="guideline-item">
                <CheckCircle2 size={20} className="check-icon" />
                <span>{g}</span>
              </div>
            ))}
          </div>

          <div className="payment-notice-box">
            <h4>Important: In-Person Fee Collection Only</h4>
            <p>
              Please note: <strong>All school fees must be paid in cash directly at the school accounts counter</strong> during office hours (Monday to Saturday, 8:00 AM – 3:00 PM). An official stamped receipt is provided immediately upon payment. <strong>No online payments, UPI QR transfers, or direct bank deposits are accepted.</strong>
            </p>
          </div>
        </section>

        <div className="fee-note-box">
          <p>
            <strong>Note:</strong> Fees are approved by the School Managing Committee and are subject to periodic revision. Please contact the administrative counter for special fee concessions or queries.
          </p>
          <p>
            For any queries regarding fee structure, please contact us at <a href="tel:7654164726">+91 76541 64726</a> or <a href="tel:9939988606">+91 99399 88606</a>.
          </p>
        </div>

      </div>
    </div>
  );
}
